import { getFreshToken } from '@/utils/auth-token-provider';
import { useOrgStore } from '@/stores/org-store';
import * as Sentry from '@sentry/react';

/**
 * One frame on the per-user event stream: something a run of the signed-in
 * user's has to tell them between chat turns. `conversation_id` names the
 * conversation that holds the relayed row (null when the run had no chat to
 * land in), so an open chat can tell its own rows from another chat's.
 */
export interface AgentUserEvent {
  type: string;
  conversation_id?: string | null;
}

export interface StreamAgentUserEventsOptions {
  orgId: string;
  agentId: string;
  signal?: AbortSignal;
  /** The stream is open; anything missed while it was not can be re-read now. */
  onOpen?: () => void;
  onEvent: (event: AgentUserEvent) => void;
  /** Stream dropped or was refused; the caller decides whether to reconnect. */
  onError: (error: string) => void;
}

// Upstream comments every 15s of silence; three missed ones is a dead pipe.
const READ_TIMEOUT_MS = 45_000;
/** The server closed the stream on purpose (it rotates them); reconnect at once. */
export const STREAM_ROTATED = 'Event stream rotated';

export function parseAgentUserEvent(raw: unknown): AgentUserEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  if (typeof d.type !== 'string' || !d.type) return null;
  const conversationId = d.conversation_id;
  return {
    type: d.type,
    conversation_id:
      conversationId === null
        ? null
        : typeof conversationId === 'string' && conversationId
          ? conversationId
          : undefined,
  };
}

export async function streamAgentUserEvents(
  opts: StreamAgentUserEventsOptions,
): Promise<void> {
  const { orgId, agentId, signal, onOpen, onEvent, onError } = opts;

  const platformToken = await getFreshToken();
  if (!platformToken) {
    onError('Not authenticated');
    return;
  }

  let env = 'prod';
  if (typeof window !== 'undefined' && window.location) {
    const searchParams = new URLSearchParams(window.location.search);
    env = searchParams.get('env')?.toLowerCase() || 'prod';
  }

  const url = `/proxy/platform/agents/${encodeURIComponent(orgId)}/${encodeURIComponent(agentId)}/events/stream?env=${env}`;

  try {
    // The same header rule apiRequest applies: an Auth0 access token goes as
    // a bearer, a platform session token as X-User-Token.
    const { authType } = useOrgStore.getState();
    const response = await fetch(url, {
      headers:
        authType === 'auth0'
          ? { Authorization: `Bearer ${platformToken}` }
          : { 'X-User-Token': platformToken },
      signal,
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      onError(
        `Event stream failed: ${response.status} ${errText.slice(0, 200)}`,
      );
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      onError('Streaming not supported in this browser');
      return;
    }

    const decoder = new TextDecoder();
    let buf = '';
    onOpen?.();

    const handleLine = (line: string): boolean => {
      if (!line.startsWith('data:')) return false;
      const raw = line.slice(5).trim();
      if (!raw) return false;
      let d: Record<string, unknown>;
      try {
        d = JSON.parse(raw);
      } catch {
        return false;
      }
      if (d.type === 'error') {
        onError((d.error as string) || 'Event stream failed');
        return true;
      }
      if (d.type === 'stream_end') {
        onError(STREAM_ROTATED);
        return true;
      }
      const event = parseAgentUserEvent(d);
      if (event) onEvent(event);
      return false;
    };

    while (true) {
      const readPromise = reader.read();
      const timeoutPromise = new Promise<'timeout'>((resolve) =>
        setTimeout(() => resolve('timeout'), READ_TIMEOUT_MS),
      );
      const result = await Promise.race([readPromise, timeoutPromise]);

      if (result === 'timeout') {
        reader.cancel().catch(() => undefined);
        onError('Event stream timed out');
        return;
      }

      const { done, value } = result;
      if (done) break;

      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith(':')) continue;
        if (handleLine(line)) return;
      }
    }

    onError('Event stream ended');
  } catch (err) {
    if (signal?.aborted) return;
    Sentry.captureException(err, {
      extra: { context: 'stream-agent-user-events', orgId, agentId },
    });
    onError(err instanceof Error ? err.message : String(err));
  }
}
